/**
 * SponsoredSlotsTab.tsx - Sponsored Slots Management for Admin Dashboard
 *
 * Features:
 * - CRUD operations for sponsored slots (6-tile affiliate grid, 2 rows × 3 cols)
 * - Affiliate partner management (Amazon, Coupang, Direct)
 * - Scheduling and targeting configuration
 * - Click/impression analytics preview
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  collection,
  query,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  doc,
  orderBy
} from 'firebase/firestore';
import { db } from '../../services/firebaseConfig';
import type { SponsoredSlot, AffiliateSource } from '../../types/product';
import './SponsoredSlotsTab.css';

interface SponsoredSlotsTabProps {
  language: string;
}

type ModalMode = 'create' | 'edit' | null;

const AFFILIATE_SOURCES: { value: AffiliateSource; label: string; emoji: string }[] = [
  { value: 'amazon', label: 'Amazon Associates', emoji: '🛒' },
  { value: 'coupang', label: 'Coupang Partners', emoji: '🇰🇷' },
  { value: 'direct', label: 'Direct Partner', emoji: '🤝' },
  { value: 'partner', label: 'Brand Partner', emoji: '💎' },
  { value: 'inhouse', label: 'Heseda (In-house)', emoji: '🏠' }
];

const SLOT_POSITIONS = [
  { value: 0, label: 'Slot 1 (Row 1 Left)' },
  { value: 1, label: 'Slot 2 (Row 1 Center)' },
  { value: 2, label: 'Slot 3 (Row 1 Right)' },
  { value: 3, label: 'Slot 4 (Row 2 Left)' },
  { value: 4, label: 'Slot 5 (Row 2 Center)' },
  { value: 5, label: 'Slot 6 (Row 2 Right)' },
];

const SponsoredSlotsTab: React.FC<SponsoredSlotsTabProps> = ({ language }) => {
  const { t } = useTranslation('admin');
  const [slots, setSlots] = useState<SponsoredSlot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [editingSlot, setEditingSlot] = useState<SponsoredSlot | null>(null);
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    slotIndex: 0,
    productId: '',
    productName: '',
    productBrand: '',
    productImage: '',
    productPrice: '',
    productDescription: '',
    affiliateUrl: '',
    affiliateSource: 'amazon' as AffiliateSource,
    cpcBid: '0.10',
    revenueShare: '0.05',
    activeFrom: '',
    activeTo: '',
    targetSkinTypes: '',
    targetConcerns: '',
    targetRegions: 'ALL',
    isActive: true
  });

  const loadSlots = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const slotsRef = collection(db, 'sponsoredSlots');
      const q = query(slotsRef, orderBy('slotIndex', 'asc'));
      const snapshot = await getDocs(q);

      const loadedSlots: SponsoredSlot[] = snapshot.docs.map(docSnap => ({
        id: docSnap.id,
        ...docSnap.data()
      })) as SponsoredSlot[];

      setSlots(loadedSlots);
    } catch (err) {
      console.error('Error loading sponsored slots:', err);
      setError(err instanceof Error ? err.message : 'Failed to load slots');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSlots();
  }, [loadSlots]);

  const resetForm = () => {
    setFormData({
      slotIndex: 0,
      productId: '',
      productName: '',
      productBrand: '',
      productImage: '',
      productPrice: '',
      productDescription: '',
      affiliateUrl: '',
      affiliateSource: 'amazon',
      cpcBid: '0.10',
      revenueShare: '0.05',
      activeFrom: new Date().toISOString().split('T')[0],
      activeTo: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
      targetSkinTypes: '',
      targetConcerns: '',
      targetRegions: 'ALL',
      isActive: true
    });
  };

  const openCreateModal = () => {
    resetForm();
    // Find first available slot index
    const usedIndices = slots.map(s => s.slotIndex);
    const availableIndex = ([0, 1, 2, 3, 4, 5] as const).find(i => !usedIndices.includes(i)) ?? 0;
    setFormData(prev => ({ ...prev, slotIndex: availableIndex }));
    setEditingSlot(null);
    setModalMode('create');
  };

  const openEditModal = (slot: SponsoredSlot) => {
    setEditingSlot(slot);
    setFormData({
      slotIndex: slot.slotIndex,
      productId: slot.product.id,
      productName: slot.product.name,
      productBrand: slot.product.brand,
      productImage: slot.product.image,
      productPrice: String(slot.product.priceUSD),
      productDescription: slot.product.description || '',
      affiliateUrl: slot.affiliateUrl,
      affiliateSource: slot.affiliateSource,
      cpcBid: String(slot.cpcBid),
      revenueShare: String(slot.revenueShare || 0.05),
      activeFrom: typeof slot.activeFrom === 'string'
        ? slot.activeFrom.split('T')[0]
        : new Date(slot.activeFrom).toISOString().split('T')[0],
      activeTo: typeof slot.activeTo === 'string'
        ? slot.activeTo.split('T')[0]
        : new Date(slot.activeTo).toISOString().split('T')[0],
      targetSkinTypes: slot.targeting?.skinTypes?.join(', ') || '',
      targetConcerns: slot.targeting?.concerns?.join(', ') || '',
      targetRegions: slot.targeting?.regions?.join(', ') || 'ALL',
      isActive: slot.isActive
    });
    setModalMode('edit');
  };

  const closeModal = () => {
    setModalMode(null);
    setEditingSlot(null);
    resetForm();
  };

  const handleSave = async () => {
    setSaving(true);
    setError(null);

    try {
      const slotData: Omit<SponsoredSlot, 'id'> = {
        slotIndex: formData.slotIndex,
        product: {
          id: formData.productId || `prod_${Date.now()}`,
          name: formData.productName,
          brand: formData.productBrand,
          image: formData.productImage,
          priceUSD: parseFloat(formData.productPrice) || 0,
          description: formData.productDescription || undefined
        },
        affiliateUrl: formData.affiliateUrl,
        affiliateSource: formData.affiliateSource,
        cpcBid: parseFloat(formData.cpcBid) || 0.10,
        revenueShare: parseFloat(formData.revenueShare) || 0.05,
        activeFrom: formData.activeFrom,
        activeTo: formData.activeTo,
        targeting: {
          skinTypes: formData.targetSkinTypes
            ? formData.targetSkinTypes.split(',').map(s => s.trim()).filter(Boolean)
            : undefined,
          concerns: formData.targetConcerns
            ? formData.targetConcerns.split(',').map(s => s.trim()).filter(Boolean)
            : undefined,
          regions: formData.targetRegions
            ? formData.targetRegions.split(',').map(s => s.trim()) as ('US' | 'KR' | 'ALL')[]
            : ['ALL']
        },
        isActive: formData.isActive,
        updatedAt: new Date().toISOString()
      };

      if (modalMode === 'create') {
        slotData.createdAt = new Date().toISOString();
        slotData.impressions = 0;
        slotData.clicks = 0;
        slotData.revenue = 0;
        await addDoc(collection(db, 'sponsoredSlots'), slotData);
      } else if (editingSlot) {
        await updateDoc(doc(db, 'sponsoredSlots', editingSlot.id), slotData);
      }

      await loadSlots();
      closeModal();
    } catch (err) {
      console.error('Error saving slot:', err);
      setError(err instanceof Error ? err.message : 'Failed to save slot');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (slot: SponsoredSlot) => {
    const confirmMsg = `Delete sponsored slot for "${slot.product.name}"?`;
    if (!confirm(confirmMsg)) {
      return;
    }

    try {
      await deleteDoc(doc(db, 'sponsoredSlots', slot.id));
      await loadSlots();
    } catch (err) {
      console.error('Error deleting slot:', err);
      setError(err instanceof Error ? err.message : 'Failed to delete slot');
    }
  };

  const handleToggleActive = async (slot: SponsoredSlot) => {
    try {
      await updateDoc(doc(db, 'sponsoredSlots', slot.id), {
        isActive: !slot.isActive,
        updatedAt: new Date().toISOString()
      });
      await loadSlots();
    } catch (err) {
      console.error('Error toggling slot:', err);
      setError(err instanceof Error ? err.message : 'Failed to update slot');
    }
  };

  const getSourceInfo = (source: AffiliateSource) => {
    return AFFILIATE_SOURCES.find(s => s.value === source) || AFFILIATE_SOURCES[0];
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatDate = (date: Date | string) => {
    const d = typeof date === 'string' ? new Date(date) : date;
    return d.toLocaleDateString(language === 'ko' ? 'ko-KR' : 'en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="sponsored-slots-tab">
        <div className="loading-spinner">Loading sponsored slots...</div>
      </div>
    );
  }

  return (
    <div className="sponsored-slots-tab">
      {/* Header */}
      <div className="sponsored-slots-header">
        <div>
          <h2>💎 Sponsored Slots (6-Tile Grid)</h2>
          <p className="header-subtitle">
            Manage affiliate and sponsored product placements for the 6-tile grid (2 rows × 3 cols)
          </p>
        </div>
        <button
          className="btn-primary"
          onClick={openCreateModal}
          disabled={slots.length >= 6}
        >
          ➕ Add Slot
        </button>
      </div>

      {error && (
        <div className="error-banner">
          ⚠️ {error}
          <button onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {/* Visual Grid Preview */}
      <div className="grid-preview">
        <div className="grid-preview-title">📊 Affiliate Grid Preview (2×3)</div>
        <div className="grid-preview-slots">
          {[0, 1, 2, 3, 4, 5].map(index => {
            const slot = slots.find(s => s.slotIndex === index);
            return (
              <div
                key={index}
                className={`grid-preview-slot ${slot ? 'filled' : 'empty'} ${slot?.isActive ? 'active' : 'inactive'}`}
              >
                {slot ? (
                  <>
                    <div className="slot-badge">
                      {getSourceInfo(slot.affiliateSource).emoji}
                    </div>
                    <img
                      src={slot.product.image}
                      alt={slot.product.name}
                      className="slot-image"
                      onError={(e) => {
                        (e.target as HTMLImageElement).src = '/placeholder.png';
                      }}
                    />
                    <div className="slot-title">{slot.product.name}</div>
                    <div className="slot-price">{formatCurrency(slot.product.priceUSD)}</div>
                    {!slot.isActive && <div className="slot-inactive-badge">PAUSED</div>}
                  </>
                ) : (
                  <div className="slot-empty-content">
                    <span>Empty Slot</span>
                    <button
                      className="btn-add-slot"
                      onClick={() => {
                        resetForm();
                        setFormData(prev => ({ ...prev, slotIndex: index }));
                        setModalMode('create');
                      }}
                    >
                      + Add
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Slots Table */}
      <div className="slots-table-container">
        <table className="slots-table">
          <thead>
            <tr>
              <th>Slot</th>
              <th>Product</th>
              <th>Source</th>
              <th>CPC Bid</th>
              <th>Schedule</th>
              <th>Stats</th>
              <th>Status</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {slots.length === 0 ? (
              <tr>
                <td colSpan={8} className="empty-state">
                  No sponsored slots configured. Click "Add Slot" to create one.
                </td>
              </tr>
            ) : (
              slots.map(slot => {
                const sourceInfo = getSourceInfo(slot.affiliateSource);
                const ctr = slot.impressions && slot.impressions > 0
                  ? ((slot.clicks || 0) / slot.impressions * 100).toFixed(1)
                  : '0.0';

                return (
                  <tr key={slot.id} className={slot.isActive ? '' : 'inactive-row'}>
                    <td>
                      <span className="slot-position">
                        {SLOT_POSITIONS[slot.slotIndex]?.label || `Slot ${slot.slotIndex + 1}`}
                      </span>
                    </td>
                    <td>
                      <div className="product-cell">
                        <img
                          src={slot.product.image}
                          alt={slot.product.name}
                          className="product-thumb"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src = '/placeholder.png';
                          }}
                        />
                        <div>
                          <div className="product-name">{slot.product.name}</div>
                          <div className="product-brand">{slot.product.brand}</div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <span className="source-badge">
                        {sourceInfo.emoji} {sourceInfo.label}
                      </span>
                    </td>
                    <td>{formatCurrency(slot.cpcBid)}</td>
                    <td>
                      <div className="schedule-cell">
                        <div>{formatDate(slot.activeFrom)}</div>
                        <div className="schedule-arrow">→</div>
                        <div>{formatDate(slot.activeTo)}</div>
                      </div>
                    </td>
                    <td>
                      <div className="stats-cell">
                        <div>👁️ {slot.impressions || 0}</div>
                        <div>👆 {slot.clicks || 0}</div>
                        <div>📈 {ctr}% CTR</div>
                      </div>
                    </td>
                    <td>
                      <button
                        className={`status-toggle ${slot.isActive ? 'active' : 'paused'}`}
                        onClick={() => handleToggleActive(slot)}
                      >
                        {slot.isActive ? '🟢 Active' : '⏸️ Paused'}
                      </button>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button
                          className="btn-icon"
                          onClick={() => openEditModal(slot)}
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          className="btn-icon btn-danger"
                          onClick={() => handleDelete(slot)}
                          title="Delete"
                        >
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Create/Edit Modal */}
      {modalMode && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{modalMode === 'create' ? '➕ Add Sponsored Slot' : '✏️ Edit Sponsored Slot'}</h3>
              <button className="modal-close" onClick={closeModal}>✕</button>
            </div>

            <div className="modal-body">
              {/* Slot Position */}
              <div className="form-group">
                <label>Slot Position *</label>
                <select
                  value={formData.slotIndex}
                  onChange={e => setFormData(prev => ({
                    ...prev,
                    slotIndex: parseInt(e.target.value)
                  }))}
                >
                  {SLOT_POSITIONS.map(pos => (
                    <option key={pos.value} value={pos.value}>
                      {pos.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Product Info Section */}
              <div className="form-section">
                <h4>Product Information</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label>Product Title *</label>
                    <input
                      type="text"
                      value={formData.productName}
                      onChange={e => setFormData(prev => ({ ...prev, productName: e.target.value }))}
                      placeholder="e.g., CeraVe Moisturizing Cream"
                    />
                  </div>
                  <div className="form-group">
                    <label>Brand *</label>
                    <input
                      type="text"
                      value={formData.productBrand}
                      onChange={e => setFormData(prev => ({ ...prev, productBrand: e.target.value }))}
                      placeholder="e.g., CeraVe"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Price (USD) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.productPrice}
                      onChange={e => setFormData(prev => ({ ...prev, productPrice: e.target.value }))}
                      placeholder="18.99"
                    />
                  </div>
                  <div className="form-group">
                    <label>Product ID</label>
                    <input
                      type="text"
                      value={formData.productId}
                      onChange={e => setFormData(prev => ({ ...prev, productId: e.target.value }))}
                      placeholder="Auto-generated if empty"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Product Image URL *</label>
                  <input
                    type="url"
                    value={formData.productImage}
                    onChange={e => setFormData(prev => ({ ...prev, productImage: e.target.value }))}
                    placeholder="https://..."
                  />
                  {formData.productImage && (
                    <img
                      src={formData.productImage}
                      alt="Preview"
                      className="image-preview"
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                </div>

                <div className="form-group">
                  <label>Description (optional)</label>
                  <textarea
                    value={formData.productDescription}
                    onChange={e => setFormData(prev => ({ ...prev, productDescription: e.target.value }))}
                    placeholder="Brief product description..."
                    rows={2}
                  />
                </div>
              </div>

              {/* Affiliate Info Section */}
              <div className="form-section">
                <h4>Affiliate &amp; Tracking</h4>

                <div className="form-row">
                  <div className="form-group">
                    <label>Affiliate Source *</label>
                    <select
                      value={formData.affiliateSource}
                      onChange={e => setFormData(prev => ({
                        ...prev,
                        affiliateSource: e.target.value as AffiliateSource
                      }))}
                    >
                      {AFFILIATE_SOURCES.map(source => (
                        <option key={source.value} value={source.value}>
                          {source.emoji} {source.label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>CPC Bid (USD) *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={formData.cpcBid}
                      onChange={e => setFormData(prev => ({ ...prev, cpcBid: e.target.value }))}
                      placeholder="0.10"
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Affiliate URL *</label>
                  <input
                    type="url"
                    value={formData.affiliateUrl}
                    onChange={e => setFormData(prev => ({ ...prev, affiliateUrl: e.target.value }))}
                    placeholder="https://amazon.com/dp/...?tag=your-tag-20"
                  />
                  <small className="form-hint">
                    Include your affiliate tracking tag (e.g., ?tag=hesedaai-20 for Amazon)
                  </small>
                </div>

                <div className="form-group">
                  <label>Revenue Share (%)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    max="1"
                    value={formData.revenueShare}
                    onChange={e => setFormData(prev => ({ ...prev, revenueShare: e.target.value }))}
                    placeholder="0.05 (5%)"
                  />
                  <small className="form-hint">
                    Enter as decimal (0.05 = 5%). Amazon Beauty: ~6-8%, Coupang: 3-7%
                  </small>
                </div>
              </div>

              {/* Schedule Section */}
              <div className="form-section">
                <h4>Schedule</h4>
                <div className="form-row">
                  <div className="form-group">
                    <label>Active From *</label>
                    <input
                      type="date"
                      value={formData.activeFrom}
                      onChange={e => setFormData(prev => ({ ...prev, activeFrom: e.target.value }))}
                    />
                  </div>
                  <div className="form-group">
                    <label>Active To *</label>
                    <input
                      type="date"
                      value={formData.activeTo}
                      onChange={e => setFormData(prev => ({ ...prev, activeTo: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              {/* Targeting Section */}
              <div className="form-section">
                <h4>Targeting (Optional)</h4>

                <div className="form-group">
                  <label>Skin Types (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.targetSkinTypes}
                    onChange={e => setFormData(prev => ({ ...prev, targetSkinTypes: e.target.value }))}
                    placeholder="dry, normal, sensitive"
                  />
                </div>

                <div className="form-group">
                  <label>Concerns (comma-separated)</label>
                  <input
                    type="text"
                    value={formData.targetConcerns}
                    onChange={e => setFormData(prev => ({ ...prev, targetConcerns: e.target.value }))}
                    placeholder="acne, aging, hydration"
                  />
                </div>

                <div className="form-group">
                  <label>Regions</label>
                  <input
                    type="text"
                    value={formData.targetRegions}
                    onChange={e => setFormData(prev => ({ ...prev, targetRegions: e.target.value }))}
                    placeholder="US, KR, ALL"
                  />
                </div>
              </div>

              {/* Active Toggle */}
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.isActive}
                    onChange={e => setFormData(prev => ({ ...prev, isActive: e.target.checked }))}
                  />
                  Slot is Active
                </label>
              </div>
            </div>

            <div className="modal-footer">
              <button className="btn-secondary" onClick={closeModal} disabled={saving}>
                Cancel
              </button>
              <button
                className="btn-primary"
                onClick={handleSave}
                disabled={saving || !formData.productName || !formData.affiliateUrl}
              >
                {saving ? 'Saving...' : (modalMode === 'create' ? 'Create Slot' : 'Save Changes')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsoredSlotsTab;
